/**
 * F-54 mention directory on the client: who can be `@`-tagged, and how a
 * half-typed `@ki` becomes a ranked list of them.
 *
 * The roster is fetched ONCE per page load and shared by every consumer (the
 * composer's autocomplete, every rendered message body, the panel's
 * "mentions me" filter) — the same module-level promise cache `useMe` uses.
 * It is small (~30 people), changes when someone joins the company, and a
 * comment popover that re-fetched it on every open would be silly.
 *
 * A failed fetch degrades to the EMPTY directory rather than an error state:
 * with no directory, `@kim` still highlights as it always did and simply
 * resolves to nobody, so an unreachable `/api/team` costs the autocomplete
 * and nothing else.
 */
import {
  buildMentionDirectory,
  EMPTY_MENTION_DIRECTORY,
  resolveMentions,
  type CommentThreadInfo,
  type MentionDirectory,
  type MentionTarget,
} from "@kola/shared";
import { useEffect, useState } from "react";

import { fetchTeam } from "../../core/api.js";

let cached: Promise<MentionDirectory> | null = null;

function loadDirectory(): Promise<MentionDirectory> {
  cached ??= fetchTeam()
    .then(buildMentionDirectory)
    .catch(() => {
      // Do NOT keep the rejected promise: the next mount retries (the roster
      // 503s until a deployment configures the task bridge, and it should
      // start working the moment one does, without a reload).
      cached = null;
      return EMPTY_MENTION_DIRECTORY;
    });
  return cached;
}

/** Test seam + HMR escape hatch: forget the cached roster. */
export function resetMentionDirectoryCache(): void {
  cached = null;
}

/**
 * The mention directory, empty until the roster arrives. Never suspends and
 * never throws — callers render fine against the empty one.
 */
export function useMentionDirectory(): MentionDirectory {
  const [directory, setDirectory] = useState<MentionDirectory>(EMPTY_MENTION_DIRECTORY);
  useEffect(() => {
    let cancelled = false;
    void loadDirectory().then((next) => {
      if (!cancelled) setDirectory(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return directory;
}

/* ------------------------------------------------------------------ */
/* Query → candidates                                                  */
/* ------------------------------------------------------------------ */

/** How many people the autocomplete offers at once. */
export const MENTION_SUGGESTION_LIMIT = 6;

/**
 * The `@` token the caret currently sits inside, or null.
 *
 * The trigger `@` must start the text or follow whitespace — typing an email
 * address must not open a people picker mid-word. The query runs from there to
 * the caret and may not contain whitespace, so a mention that has already been
 * accepted (and had its trailing space typed) closes the picker.
 */
export function mentionQueryAt(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  const preceding = at === 0 ? "" : before.charAt(at - 1);
  if (preceding !== "" && !/\s/.test(preceding)) return null;
  const query = before.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, query };
}

/**
 * Rank the roster against a query. Matches are prefix-first (typing "ki"
 * should put "Kim" above "Yuki"), then substring, over both the display name
 * and the handle; ties keep the directory's display-name order.
 */
export function suggestMentions(
  directory: MentionDirectory,
  query: string,
  limit = MENTION_SUGGESTION_LIMIT,
): MentionTarget[] {
  const needle = query.trim().toLowerCase();
  const scored: Array<{ target: MentionTarget; score: number }> = [];
  for (const target of directory.targets) {
    if (target.handle === "") continue;
    if (needle === "") {
      scored.push({ target, score: 2 });
      continue;
    }
    const name = target.displayName.toLowerCase();
    const handle = target.handle;
    // Word-start match on the display name too, so "lee" finds "Kim Lee".
    const nameWordStart = name === needle || name.split(/\s+/).some((w) => w.startsWith(needle));
    if (handle.startsWith(needle) || nameWordStart) scored.push({ target, score: 0 });
    else if (handle.includes(needle) || name.includes(needle)) scored.push({ target, score: 1 });
  }
  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, limit).map((s) => s.target);
}

/**
 * Replace the `@query` the caret sits in with `@handle `, and report where the
 * caret should land. The trailing space is what CLOSES the picker (a query
 * cannot contain whitespace) and is what the next word needs anyway.
 */
export function applyMention(
  text: string,
  caret: number,
  start: number,
  handle: string,
): { text: string; caret: number } {
  const inserted = `@${handle} `;
  return {
    text: text.slice(0, start) + inserted + text.slice(caret),
    caret: start + inserted.length,
  };
}

/* ------------------------------------------------------------------ */
/* "Is this thread about me?"                                          */
/* ------------------------------------------------------------------ */

/**
 * Is this mention me? `currentUser().id` is a Firebase uid in production and
 * the canonical email in dev auth (core/identity.ts), and the roster carries
 * both — so match on either rather than betting on one convention.
 */
export function mentionIsMe(target: MentionTarget | undefined, meId: string): boolean {
  return target !== undefined && (target.uid === meId || target.email === meId);
}

/**
 * Does any message in this thread `@`-tag me?
 *
 * Shared by the panel's "@me" filter and the pin's tagged marker so the two
 * can never disagree about which threads are addressed to the reader — the
 * board would be telling you a thread wants you while the panel's filter hid
 * it. Derived from the bodies through the directory, like every other mention
 * read (docs/04 §2: mentions are never stored).
 */
export function threadMentionsMe(
  thread: CommentThreadInfo,
  directory: MentionDirectory,
  meId: string,
): boolean {
  return thread.messages.some((message) =>
    resolveMentions(message.body, directory).some((target) => mentionIsMe(target, meId)),
  );
}
